import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import Swal from 'sweetalert2';

import { JOBS } from 'lib/networking/endpoints';
import { JOB_MODULE_ALLOWLIST_PREFIXES } from 'lib/enums/jobConstants';
import usePost from 'lib/hooks/usePost';
import usePut from 'lib/hooks/usePut';

const CATEGORY_OPTIONS = ['EXPORT', 'IMPORT', 'PROCESS'];

function NewJobModal({ job, onHide, refetch }) {
  const isEdit = Boolean(job);

  const [form, setForm] = useState({
    name: job?.name || '',
    description: job?.description || '',
    category: job?.category || 'EXPORT',
    module_name: job?.module_name || '',
    class_name: job?.class_name || '',
    method: job?.method || '',
    default_params: JSON.stringify(job?.default_params || {}, null, 2),
    task_type: job?.task_type || 'JOB_EXECUTION',
    enabled: job?.enabled !== undefined ? job.enabled : true,
    cron_expression: job?.cron_expression || '',
  });

  const [errors, setErrors] = useState({});

  const { postData: createJob, loading: creating } = usePost(
    JOBS,
    () => {
      Swal.fire({ icon: 'success', title: 'Success', text: 'Job created successfully.' }).then(
        () => {
          refetch();
          onHide();
        },
      );
    },
    () => {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create job.' });
    },
  );

  const { putData: updateJob, loading: updating } = usePut(
    job ? `${JOBS}/${job.id}` : null,
    () => {
      Swal.fire({ icon: 'success', title: 'Success', text: 'Job updated successfully.' }).then(
        () => {
          refetch();
          onHide();
        },
      );
    },
    () => {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update job.' });
    },
  );

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required.';
    if (!form.module_name.trim()) {
      newErrors.module_name = 'Module name is required.';
    } else if (
      !JOB_MODULE_ALLOWLIST_PREFIXES.some(prefix => form.module_name.trim().startsWith(prefix))
    ) {
      newErrors.module_name = `Module must start with one of: ${JOB_MODULE_ALLOWLIST_PREFIXES.join(
        ', ',
      )}`;
    }
    if (!form.class_name.trim()) newErrors.class_name = 'Class name is required.';
    if (!form.method.trim()) newErrors.method = 'Method is required.';
    try {
      JSON.parse(form.default_params || '{}');
    } catch {
      newErrors.default_params = 'Invalid JSON.';
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      category: form.category,
      // Trimmed on the way out as well as in validation: a pasted value carrying a space
      // failed the allowlist check while reading correctly on screen, and a trailing space on
      // the class or method passed validation and became an AttributeError at run time.
      module_name: form.module_name.trim(),
      class_name: form.class_name.trim(),
      method: form.method.trim(),
      default_params: JSON.parse(form.default_params || '{}'),
      task_type: form.task_type,
      enabled: form.enabled,
      cron_expression: form.cron_expression || null,
    };

    if (isEdit) {
      updateJob(payload);
    } else {
      createJob(payload);
    }
  };

  const update = field => e =>
    setForm(prev => ({
      ...prev,
      [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const loading = creating || updating;

  return (
    <Modal show centered onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Job' : 'New Job'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={update('name')}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Category *</Form.Label>
                <Form.Control as="select" value={form.category} onChange={update('category')}>
                  {CATEGORY_OPTIONS.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={update('description')}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Module Name *</Form.Label>
            <Form.Control
              value={form.module_name}
              onChange={update('module_name')}
              placeholder="app.data_exporters.integrations.integration_vendor_product_export"
              isInvalid={!!errors.module_name}
            />
            <Form.Text className="text-muted">
              Must start with one of: {JOB_MODULE_ALLOWLIST_PREFIXES.join(', ')}
            </Form.Text>
            <Form.Control.Feedback type="invalid">{errors.module_name}</Form.Control.Feedback>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Class Name *</Form.Label>
                <Form.Control
                  value={form.class_name}
                  onChange={update('class_name')}
                  placeholder="IntegrationVendorProductExport"
                  isInvalid={!!errors.class_name}
                />
                <Form.Control.Feedback type="invalid">{errors.class_name}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Method *</Form.Label>
                <Form.Control
                  value={form.method}
                  onChange={update('method')}
                  placeholder="index"
                  isInvalid={!!errors.method}
                />
                <Form.Control.Feedback type="invalid">{errors.method}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group>
            <Form.Label>Default Parameters (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={form.default_params}
              onChange={update('default_params')}
              isInvalid={!!errors.default_params}
            />
            <Form.Control.Feedback type="invalid">{errors.default_params}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Cron Expression</Form.Label>
            <Form.Control
              value={form.cron_expression}
              onChange={update('cron_expression')}
              placeholder="0 6 * * * (optional — leave blank for manual-only)"
            />
            <Form.Text className="text-muted">
              Leave blank for a manual-only job. Example: <code>0 6 * * *</code> = daily at 6am.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="checkbox"
              label="Enabled"
              checked={form.enabled}
              onChange={update('enabled')}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Job'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

NewJobModal.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
    module_name: PropTypes.string,
    class_name: PropTypes.string,
    method: PropTypes.string,
    default_params: PropTypes.object,
    task_type: PropTypes.string,
    enabled: PropTypes.bool,
    cron_expression: PropTypes.string,
  }),
  onHide: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default NewJobModal;
